import React, { useEffect, useRef, useState } from "react";
import { useLoaderData, useNavigate, Form } from "react-router-dom";
import {
  getEntities,
  getBooking,
  addBooking,
  updateBooking,
  deleteBooking,
  uploadBookingItemPicture,
  SERVER_API,
} from "../../API";
import { PROVINCES } from "../../Constants";
import InputFormField from "../../components/InputFormField";
import SelectFormField from "../../components/SelectFormField";
import ItemsSelector from "../../components/ItemsSelector";
import DropdownMultiSelect from "../../components/DropdownMultiSelect";
import UpdateDeleteBar from "../../components/UpdateDeleteBar";

export async function loader({ params }) {
  const [manufacturersResp, territoriesResp, bannersResp, representativesResp] =
    await Promise.all([
      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
      getEntities("territories", "?size=1000"),
      getEntities("banners", "?size=1000"),
      getEntities("representatives", "?size=1000"),
    ]);

  const byName = (a, b) => a.name.localeCompare(b.name);
  const allManufacturers = (manufacturersResp?.content ?? []).sort(byName);
  const allTerritories = territoriesResp?.content ?? [];
  const allBanners = (bannersResp?.content ?? []).sort(byName);
  const allRepresentatives = (representativesResp?.content ?? []).sort((a, b) => (a.lastName ?? "").localeCompare(b.lastName ?? "") || (a.firstName ?? "").localeCompare(b.firstName ?? ""));

  let booking;
  if (params.id) {
    booking = await getBooking(params.id);
    if (booking.startDate)
      booking.startDate = new Date(booking.startDate).toISOString().slice(0, 10);
    if (booking.endDate)
      booking.endDate = new Date(booking.endDate).toISOString().slice(0, 10);
    booking.representatives = (booking.representatives ?? [])
      .map((r) => {
        const repId = r.bookingRepresentativeKey?.representativeId ?? r.id ?? 0;
        return allRepresentatives.find((rep) => rep.id === repId);
      })
      .filter(Boolean);
    booking.stores = (booking.stores ?? []).map((s) => ({
      id: s.bookingStoreKey?.storeId ?? s.id ?? 0,
    }));
    booking.items = booking.items ?? [];
  } else {
    booking = {
      name: "",
      startDate: "",
      endDate: "",
      manufacturer: null,
      provinces: [],
      territories: [],
      banners: [],
      stores: [],
      representatives: [],
      items: [],
    };
  }

  return { booking, allManufacturers, allTerritories, allBanners, allRepresentatives };
}

function Booking() {
  const { booking: initialBooking, allManufacturers, allTerritories, allBanners, allRepresentatives } =
    useLoaderData();

  const navigate = useNavigate();
  const refForm = useRef();

  const [booking, setBooking] = useState(initialBooking);
  const [items, setItems] = useState(initialBooking.items ?? []);
  const [errors, setErrors] = useState({});
  const [savedBookingId, setSavedBookingId] = useState(initialBooking.id ?? null);

  const [filteredTerritories, setFilteredTerritories] = useState([]);
  const [filteredRepresentatives, setFilteredRepresentatives] = useState([]);
  const [filteredBanners, setFilteredBanners] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);

  useEffect(() => {
    handleProvinceSelection(booking.provinces, false);
    handleTerritorySelection(booking.territories, false);
    handleBannerSelection(booking.banners, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProvinceSelection = (provinces, updateState = true) => {
    const territories = allTerritories.filter((t) =>
      provinces.find((p) => p.id == t.province?.id)
    );
    setFilteredTerritories(territories);
    if (updateState) {
      setBooking({ ...booking, provinces, territories: [], representatives: [], banners: [], stores: [] });
      setFilteredRepresentatives([]);
      setFilteredBanners([]);
      setFilteredStores([]);
    }
  };

  const handleTerritorySelection = (territories, updateState = true) => {
    const reps = allRepresentatives.filter((r) =>
      territories.find((t) => (r.territories ?? []).find((t2) => t2.id == t.id))
    );
    const banners = allBanners.filter((b) =>
      territories.find((t) => (b.provinces ?? []).find((p) => p.id == t.province?.id))
    );
    setFilteredRepresentatives(reps);
    setFilteredBanners(banners);
    setFilteredStores([]);
    if (updateState) {
      setBooking({ ...booking, territories, representatives: [], banners: [], stores: [] });
    }
  };

  const handleBannerSelection = (banners, updateState = true) => {
    if (updateState) {
      setBooking({ ...booking, banners, stores: [] });
    }
    if (banners.length === 0) { setFilteredStores([]); return; }
    const bannerIds = banners.map((b) => `bannerIds=${b.id}`).join("&");
    const territoryIds = booking.territories.map((t) => `territoryIds=${t.id}`).join("&");
    getEntities("stores/by-banners-and-territories", `?${bannerIds}&${territoryIds}&size=1000`)
      .then((resp) => setFilteredStores(resp?.content ?? []));
  };

  const submit = () =>
    refForm.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));

  const validate = (b) => {
    const errs = {};
    if (!b.name) errs.name = "Required";
    if (!b.startDate) errs.startDate = "Required";
    if (!b.endDate) errs.endDate = "Required";
    if (!b.manufacturer) errs.manufacturer = "Required";
    return Object.keys(errs).length > 0 ? errs : null;
  };

  const itemSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get("name");
    const startDate = formData.get("startDate");
    const endDate = formData.get("endDate");

    const validationErrors = validate({ name, startDate, endDate, manufacturer: booking.manufacturer });
    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const payload = {
      name,
      startDate: new Date(startDate).getTime(),
      endDate: new Date(endDate).getTime(),
      manufacturer: booking.manufacturer,
      provinces: booking.provinces,
      territories: booking.territories,
      banners: booking.banners,
      stores: (booking.stores ?? []).map((s) => ({
        bookingStoreKey: { storeId: s.id ?? s.bookingStoreKey?.storeId ?? 0 },
      })),
      representatives: (booking.representatives ?? []).map((r) => ({
        bookingRepresentativeKey: { representativeId: r.id ?? r.bookingRepresentativeKey?.representativeId ?? 0 },
      })),
      items: items.map((item) => ({
        id: item.id ?? 0,
        description: item.description,
        orderNumber: item.orderNumber ?? "",
        upcCode: item.upcCode ?? "",
        pictureFileName: item.pictureFileName ?? "",
      })),
    };

    if (booking.id) {
      await updateBooking(booking.id, payload);
      setSavedBookingId(booking.id);
    } else {
      await addBooking(payload);
    }
    navigate("../bookings");
  };

  const itemDelete = async () => {
    await deleteBooking(booking.id);
    navigate("../bookings");
  };

  const addItem = () =>
    setItems([...items, { description: "", upcCode: "", pictureFileName: "" }]);

  const updateItem = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    setItems(next);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const handlePictureUpload = async (idx, file) => {
    const item = items[idx];
    if (!item.id || !booking.id) {
      alert("Save the booking first before uploading pictures.");
      return;
    }
    const updated = await uploadBookingItemPicture(booking.id, item.id, file);
    const next = [...items];
    next[idx] = { ...next[idx], pictureFileName: updated.pictureFileName };
    setItems(next);
  };

  const isEdit = !!booking.id;

  return (
    <div className="flex-grow-1 overflow-auto">
      <UpdateDeleteBar
        onDelete={isEdit ? itemDelete : undefined}
        onSave={submit}
        title={booking.name || "New Booking"}
      />
      <div className="container-fluid py-4 px-4">
        <Form onSubmit={itemSave} ref={refForm}>

          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <InputFormField label="Name" name="name" value={booking.name} error={errors.name} />
                </div>
                <div className="col-md-3">
                  <InputFormField label="Start Date" name="startDate" type="date" value={booking.startDate} error={errors.startDate} />
                </div>
                <div className="col-md-3">
                  <InputFormField label="End Date" name="endDate" type="date" value={booking.endDate} error={errors.endDate} />
                </div>
                <div className="col-md-6">
                  <SelectFormField
                    label="Manufacturer"
                    value={booking.manufacturer}
                    options={allManufacturers}
                    onChange={(m) => setBooking({ ...booking, manufacturer: m })}
                    objectValue={true}
                    error={errors.manufacturer}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-medium mb-1">Provinces</label>
                  <DropdownMultiSelect
                    options={PROVINCES}
                    value={booking.provinces}
                    onChange={handleProvinceSelection}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    placeholder="Select provinces…"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-2">
                <ItemsSelector
                  name="Territories"
                  values={booking.territories}
                  items={filteredTerritories}
                  fields={["name"]}
                  enable={filteredTerritories.length > 0}
                  onSave={handleTerritorySelection}
                />
                <ItemsSelector
                  name="Representatives"
                  values={booking.representatives}
                  items={filteredRepresentatives}
                  fields={["lastName", "firstName"]}
                  enable={filteredRepresentatives.length > 0}
                  onSave={(reps) => setBooking({ ...booking, representatives: reps })}
                />
                <ItemsSelector
                  name="Banners"
                  values={booking.banners}
                  items={filteredBanners}
                  fields={["name"]}
                  enable={filteredBanners.length > 0}
                  onSave={handleBannerSelection}
                />
                <ItemsSelector
                  name="Stores"
                  values={booking.stores}
                  items={filteredStores}
                  fields={["name", "number", "civicNo", "street", "city"]}
                  enable={filteredStores.length > 0}
                  onSave={(stores) => setBooking({ ...booking, stores })}
                />
              </div>
            </div>
          </div>

        </Form>

        <div className="card shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 fw-semibold">Booking Items</h6>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addItem}>
                + Add Item
              </button>
            </div>
            {items.length === 0 && (
              <p className="text-muted small mb-0">No items yet. Click "Add Item" to add products to this booking.</p>
            )}
            {items.map((item, idx) => (
              <div key={idx} className="border rounded p-3 mb-3">
                <div className="row g-2 align-items-start">
                  <div className="col-md-4">
                    <label className="form-label small">Description</label>
                    <input
                      className="form-control form-control-sm"
                      value={item.description}
                      onChange={(e) => updateItem(idx, "description", e.target.value)}
                      placeholder="Product description"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small">Order #</label>
                    <input
                      className="form-control form-control-sm"
                      value={item.orderNumber ?? ""}
                      onChange={(e) => updateItem(idx, "orderNumber", e.target.value)}
                      placeholder="Order number"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small">UPC Code</label>
                    <input
                      className="form-control form-control-sm"
                      value={item.upcCode}
                      onChange={(e) => updateItem(idx, "upcCode", e.target.value)}
                      placeholder="UPC"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Picture</label>
                    {item.pictureFileName ? (
                      <div>
                        <img
                          src={`${SERVER_API}/bookings/pictures/${item.pictureFileName}`}
                          alt="item"
                          style={{ maxHeight: 60, maxWidth: 80, objectFit: "contain" }}
                          className="d-block mb-1"
                        />
                        <input
                          type="file"
                          className="form-control form-control-sm"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handlePictureUpload(idx, e.target.files[0])}
                        />
                      </div>
                    ) : (
                      <input
                        type="file"
                        className="form-control form-control-sm"
                        accept="image/*"
                        onChange={(e) => e.target.files[0] && handlePictureUpload(idx, e.target.files[0])}
                      />
                    )}
                  </div>
                  <div className="col-md-1 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeItem(idx)}
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

export default Booking;
